import { type JSX, Show, splitProps } from 'solid-js'
import { cn } from '../lib/cn.ts'
import { tv, type VariantProps } from '../lib/tv.ts'
import { Badge } from './badge.tsx'
import { Button } from './button.tsx'
import { SparkIcon } from './icons.tsx'

/**
 * THE AI pattern for this system. Every model output — an extracted waybill, a proposed BOM, a
 * suggested stage advance — arrives inside one of these and cannot become real data until a
 * person presses Підтвердити.
 *
 * This exists because AGENTS.md ground rule 2 ("never post AI-extracted data without human
 * confirmation") has NO mechanical guard: `apps/api` depends on both `@veem/ai` and `@veem/db`,
 * so nothing stops a handler reading `result.data.lines` and inserting `stock_moves` in the same
 * function, and `needsReview: true` is read by nothing. A shared component that makes the
 * confirmation step the only path to the write is the closest thing to a guard we can build in
 * the UI layer. It does not enforce anything on the server — that stays a review question.
 *
 * The review state is carried by the explicit badge, provenance and left status rail. The values
 * stay on a quiet readable surface: marking the entire body as provisional made dense documents
 * harder to inspect, which is the opposite of what a human-review step needs.
 */
const aiDraft = tv({
  slots: {
    root: [
      'relative overflow-hidden rounded-panel border bg-surface',
      'transition-colors duration-(--duration-fast) ease-out',
    ],
    rail: 'absolute inset-y-0 left-0 w-1',
    header: 'flex items-start justify-between gap-3 px-4 pt-4 pb-3',
    identity: 'flex min-w-0 items-start gap-3',
    mark: 'grid size-8 shrink-0 place-items-center rounded-control',
    heading: 'grid min-w-0 gap-0.5',
    title: 'text-ui font-semibold tracking-tight text-text',
    meta: 'truncate text-body text-text-subtle',
    review: 'grid shrink-0 justify-items-end gap-1',
    confidence: 'font-mono text-micro text-text-subtle tabular-nums',
    body: 'mx-3 rounded-card border border-border bg-surface-muted p-3',
    footer:
      'mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-border bg-surface-muted px-4 py-2.5',
    notice: 'flex items-center gap-2 text-body text-text-muted',
    noticeDot: 'size-1.5 shrink-0 rounded-pill',
    actions: 'flex shrink-0 items-center gap-2',
  },
  variants: {
    status: {
      /** The model is still working. No actions offered — there is nothing to confirm yet. */
      generating: {
        root: 'border-draft-border',
        rail: 'bg-draft',
        mark: 'bg-draft-soft text-draft',
        noticeDot: 'animate-[pulse-soft_1.4s_var(--ease-in-out)_infinite] bg-draft',
      },
      /** Waiting on a person. The only state in which the write path is reachable. */
      review: {
        root: 'border-draft-border',
        rail: 'texture-draft',
        mark: 'bg-draft-soft text-draft',
        body: 'border-draft-border/45 bg-surface',
        noticeDot: 'bg-draft',
      },
      /** A person accepted it. It is ordinary data now and stops looking provisional. */
      confirmed: {
        root: 'border-status-done/35',
        rail: 'bg-status-done',
        mark: 'bg-status-done-soft text-status-done',
        noticeDot: 'bg-status-done',
      },
    },
  },
  defaultVariants: { status: 'review' },
})

export interface AiDraftProps
  extends Omit<JSX.HTMLAttributes<HTMLElement>, 'title'>,
    VariantProps<typeof aiDraft> {
  title: string
  /** Where it came from — a file name, a message, an order. Provenance is not optional. */
  source?: string
  /** 0–1. Shown as a percentage; omit rather than invent one. */
  confidence?: number
  confirmLabel?: string
  discardLabel?: string
  pending?: boolean
  onConfirm?: () => void
  onDiscard?: () => void
  children: JSX.Element
}

export const AiDraft = (props: AiDraftProps) => {
  const [local, rest] = splitProps(props, [
    'title',
    'source',
    'confidence',
    'status',
    'confirmLabel',
    'discardLabel',
    'pending',
    'onConfirm',
    'onDiscard',
    'children',
    'class',
  ])
  const status = () => local.status ?? 'review'
  const styles = () => aiDraft({ status: status() })
  const statusLabel = () =>
    status() === 'generating'
      ? 'Обробка'
      : status() === 'confirmed'
        ? 'Підтверджено'
        : 'Чернетка ШІ'

  return (
    <section
      {...rest}
      class={cn(styles().root(), local.class)}
      aria-busy={status() === 'generating' || local.pending}
    >
      <span class={styles().rail()} aria-hidden="true" />
      <header class={styles().header()}>
        <div class={styles().identity()}>
          <span class={styles().mark()}>
            <span
              class={
                status() === 'generating' ? 'animate-[pulse-soft_1.4s_ease-in-out_infinite]' : ''
              }
            >
              <SparkIcon />
            </span>
          </span>
          <div class={styles().heading()}>
            <h2 class={styles().title()}>{local.title}</h2>
            <Show when={local.source}>
              {(source) => <p class={styles().meta()}>Джерело: {source()}</p>}
            </Show>
          </div>
        </div>
        <div class={styles().review()}>
          <Badge variant={status() === 'confirmed' ? 'done' : 'draft'}>{statusLabel()}</Badge>
          <Show when={local.confidence !== undefined}>
            <span class={styles().confidence()}>
              Впевненість {Math.round((local.confidence ?? 0) * 100)}%
            </span>
          </Show>
        </div>
      </header>

      <div class={styles().body()}>{local.children}</div>

      <footer class={styles().footer()}>
        <p class={styles().notice()}>
          <span class={styles().noticeDot()} aria-hidden="true" />
          {status() === 'generating'
            ? 'Модель обробляє документ…'
            : status() === 'confirmed'
              ? 'Підтверджено людиною — дані внесено.'
              : 'Чернетка. Перевірте значення — до підтвердження нічого не записано.'}
        </p>
        <Show when={status() === 'review'}>
          <div class={styles().actions()}>
            <Button
              variant="ghost"
              size="sm"
              disabled={local.pending}
              onClick={() => local.onDiscard?.()}
            >
              {local.discardLabel ?? 'Відхилити'}
            </Button>
            <Button size="sm" disabled={local.pending} onClick={() => local.onConfirm?.()}>
              {local.pending ? 'Збереження…' : (local.confirmLabel ?? 'Підтвердити')}
            </Button>
          </div>
        </Show>
      </footer>
    </section>
  )
}
